import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { zpanelApi } from '../../utils/zpanelApi';

const statusOptions = [
    { value: 'active', label: 'Aktif' },
    { value: 'suspended', label: 'Askıda' },
    { value: 'cancelled', label: 'İptal' }
];
const planOptions = [
    { value: 'free', label: 'Ücretsiz' },
    { value: 'basic', label: 'Temel' },
    { value: 'premium', label: 'Premium' }
];
const roleOptions = [
    { value: 'owner', label: 'Sahip' },
    { value: 'manager', label: 'Yönetici' },
    { value: 'staff', label: 'Personel' }
];

const OrganizationDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [org, setOrg] = useState(null);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [slugEdit, setSlugEdit] = useState('');
    const [slugSaving, setSlugSaving] = useState(false);
    const [plans, setPlans] = useState([]);
    const [addUserForm, setAddUserForm] = useState({
        email: '', password: '', first_name: '', last_name: '', phone: '', job_title: '', role: 'owner', is_payment_responsible: true
    });
    const [addingUser, setAddingUser] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [addRestaurantForm, setAddRestaurantForm] = useState({ name: '', slug: '', description: '', phone: '', email: '', address: '', status: 'active' });
    const [editingRestaurant, setEditingRestaurant] = useState(null);

    useEffect(() => {
        if (!isNew) fetchOrg();
        else setOrg({});
    }, [id, isNew]);

    useEffect(() => {
        zpanelApi.listSubscriptionPlans().then(r => setPlans(r.data || [])).catch(() => {});
    }, []);

    const fetchOrg = async () => {
        setLoading(true);
        try {
            const res = await zpanelApi.getOrganization(id);
            setOrg(res.data);
            setSlugEdit(res.data?.slug || '');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!org) return;
        setSaving(true);
        setError(null);
        try {
            if (isNew) {
                const res = await zpanelApi.createOrganization(org);
                navigate(`/zpanel/organizations/${res.data.id}`);
            } else {
                await zpanelApi.updateOrganization(id, org);
                setOrg(prev => ({ ...prev, ...org }));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSlugSave = async () => {
        if (!slugEdit.trim()) return;
        setSlugSaving(true);
        try {
            const res = await zpanelApi.updateSlug(id, slugEdit.trim());
            setOrg(prev => ({ ...prev, slug: res.data.slug }));
        } catch (err) {
            setError(err.message);
        } finally {
            setSlugSaving(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!addUserForm.email) return;
        setAddingUser(true);
        setError(null);
        try {
            const res = await zpanelApi.addUser(id, addUserForm);
            setOrg(prev => ({ ...prev, members: res.data.members }));
            setAddUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', job_title: '', role: 'owner', is_payment_responsible: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setAddingUser(false);
        }
    };

    const handleRemoveUser = async (userId) => {
        if (!confirm('Bu kullanıcıyı organizasyondan çıkaracaksınız. Devam?')) return;
        try {
            await zpanelApi.removeUser(id, userId);
            setOrg(prev => ({ ...prev, members: prev.members.filter(m => m.user_id !== userId) }));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleUpdateUser = async (userId, data) => {
        try {
            await zpanelApi.updateUser(id, userId, data);
            setOrg(prev => ({ ...prev, members: prev.members.map(m => m.user_id === userId ? { ...m, ...data } : m) }));
            setEditingUser(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteOrg = async () => {
        if (!confirm('Bu organizasyonu iptal edeceksiniz. Devam?')) return;
        try {
            await zpanelApi.deleteOrganization(id);
            navigate('/zpanel');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAddRestaurant = async (e) => {
        e.preventDefault();
        if (!addRestaurantForm.name) return;
        try {
            const res = await zpanelApi.createRestaurant(id, addRestaurantForm);
            setOrg(prev => ({ ...prev, restaurants: [...(prev.restaurants || []), res.data] }));
            setAddRestaurantForm({ name: '', slug: '', description: '', phone: '', email: '', address: '', status: 'active' });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleUpdateRestaurant = async (restaurantId, data) => {
        try {
            const res = await zpanelApi.updateRestaurant(id, restaurantId, data);
            setOrg(prev => ({ ...prev, restaurants: prev.restaurants.map(r => r.id === parseInt(restaurantId) ? res.data : r) }));
            setEditingRestaurant(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteRestaurant = async (restaurantId) => {
        if (!confirm('Bu restoranı silmek istediğinize emin misiniz?')) return;
        try {
            await zpanelApi.deleteRestaurant(id, restaurantId);
            setOrg(prev => ({ ...prev, restaurants: prev.restaurants.filter(r => r.id !== parseInt(restaurantId)) }));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleChange = (key, value) => {
        setOrg(prev => ({ ...prev, [key]: value }));
    };

    if (loading && !org) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl">
            <button onClick={() => navigate('/zpanel')} className="text-slate-400 hover:text-white mb-4 text-sm">
                ← Organizasyonlara dön
            </button>

            <h1 className="text-2xl font-bold text-white mb-6">
                {isNew ? 'Yeni Organizasyon' : org?.name || 'Organizasyon'}
            </h1>

            {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg text-sm">
                    {error}
                </div>
            )}

            <form onSubmit={handleSave} className="space-y-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Temel Bilgiler</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Ad *</label>
                            <input
                                value={org?.name || ''}
                                onChange={(e) => handleChange('name', e.target.value)}
                                required
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Ünvan</label>
                            <input
                                value={org?.title || ''}
                                onChange={(e) => handleChange('title', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                                placeholder="Fatura ünvanı"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Plan</label>
                            <select
                                value={org?.plan_type || org?.subscription_plan_id || 'free'}
                                onChange={(e) => handleChange('plan_type', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            >
                                {plans.length > 0 ? plans.map(p => <option key={p.id} value={p.code}>{p.name} ({p.price} {p.currency})</option>) : planOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Durum</label>
                            <select
                                value={org?.status || 'active'}
                                onChange={(e) => handleChange('status', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            >
                                {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Abonelik Bitiş</label>
                            <input
                                type="date"
                                value={org?.expires_at ? org.expires_at.slice(0, 10) : ''}
                                onChange={(e) => handleChange('expires_at', e.target.value || null)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Fatura Bilgileri</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Fatura Adı/Ünvan</label>
                            <input
                                value={org?.invoice_name || ''}
                                onChange={(e) => handleChange('invoice_name', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Vergi No</label>
                            <input
                                value={org?.invoice_tax_number || ''}
                                onChange={(e) => handleChange('invoice_tax_number', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-slate-400 mb-1">Vergi Dairesi</label>
                            <input
                                value={org?.invoice_tax_office || ''}
                                onChange={(e) => handleChange('invoice_tax_office', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm text-slate-400 mb-1">Fatura Adresi</label>
                            <textarea
                                value={org?.invoice_address || ''}
                                onChange={(e) => handleChange('invoice_address', e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            />
                        </div>
                    </div>
                </div>

                {!isNew && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Slug</h2>
                        <p className="text-sm text-slate-400 mb-2">Menü URL: <code className="text-amber-400">https://menu.zehuma.com/{slugEdit || org?.slug}</code></p>
                        <div className="flex gap-2">
                            <input
                                value={slugEdit}
                                onChange={(e) => setSlugEdit(e.target.value)}
                                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                                placeholder="slug"
                            />
                            <button
                                type="button"
                                onClick={handleSlugSave}
                                disabled={slugSaving || slugEdit === org?.slug}
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg disabled:opacity-50"
                            >
                                {slugSaving ? 'Kaydediliyor...' : 'Slug Güncelle'}
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Slug güncellenince slugs.htaccess otomatik güncellenir.</p>
                    </div>
                )}

                <div className="flex gap-2 flex-wrap">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    {!isNew && (
                        <>
                            <a
                                href={`https://menu.zehuma.com/${org?.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                            >
                                Menüyü Aç →
                            </a>
                            <button
                                type="button"
                                onClick={handleDeleteOrg}
                                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/50 rounded-lg"
                            >
                                Organizasyonu İptal Et
                            </button>
                        </>
                    )}
                </div>
            </form>

            {!isNew && org?.restaurants !== undefined && (
                <div className="mt-8 bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Restoranlar</h2>

                    <form onSubmit={handleAddRestaurant} className="mb-6 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                        <h3 className="text-sm font-medium text-slate-300 mb-3">Yeni Restoran Ekle</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <input value={addRestaurantForm.name} onChange={(e) => setAddRestaurantForm({ ...addRestaurantForm, name: e.target.value })} required placeholder="Restoran Adı *" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500" />
                            <input value={addRestaurantForm.slug} onChange={(e) => setAddRestaurantForm({ ...addRestaurantForm, slug: e.target.value })} placeholder="Slug (opsiyonel)" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500" />
                            <input value={addRestaurantForm.phone} onChange={(e) => setAddRestaurantForm({ ...addRestaurantForm, phone: e.target.value })} placeholder="Telefon" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500" />
                            <input value={addRestaurantForm.email} onChange={(e) => setAddRestaurantForm({ ...addRestaurantForm, email: e.target.value })} placeholder="E-posta" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500" />
                        </div>
                        <button type="submit" className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm">Ekle</button>
                    </form>

                    <table className="w-full">
                        <thead>
                            <tr className="text-left text-slate-400 text-sm">
                                <th className="pb-2">Ad</th>
                                <th className="pb-2">Slug</th>
                                <th className="pb-2">Durum</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {org.restaurants.map((r) => (
                                <tr key={r.id} className="border-t border-slate-700">
                                    {editingRestaurant?.id === r.id ? (
                                        <td colSpan={4} className="py-2">
                                            <div className="flex gap-2 flex-wrap">
                                                <input value={editingRestaurant.name} onChange={(e) => setEditingRestaurant({ ...editingRestaurant, name: e.target.value })} className="px-2 py-1 bg-slate-700 rounded text-white text-sm w-32" placeholder="Ad" />
                                                <input value={editingRestaurant.slug} onChange={(e) => setEditingRestaurant({ ...editingRestaurant, slug: e.target.value })} className="px-2 py-1 bg-slate-700 rounded text-white text-sm w-24" placeholder="Slug" />
                                                <button onClick={() => handleUpdateRestaurant(r.id, editingRestaurant)} className="px-2 py-1 bg-amber-600 text-white rounded text-xs">Kaydet</button>
                                                <button onClick={() => setEditingRestaurant(null)} className="px-2 py-1 bg-slate-600 text-white rounded text-xs">İptal</button>
                                            </div>
                                        </td>
                                    ) : (
                                        <>
                                            <td className="py-2 text-white">{r.name}</td>
                                            <td className="py-2 text-amber-400">{r.slug}</td>
                                            <td className="py-2 text-slate-400">{r.status}</td>
                                            <td className="py-2">
                                                <button onClick={() => setEditingRestaurant({ ...r })} className="text-amber-400 hover:text-amber-300 text-sm mr-2">Düzenle</button>
                                                <button onClick={() => handleDeleteRestaurant(r.id)} className="text-red-400 hover:text-red-300 text-sm" disabled={org.restaurants.length <= 1}>Sil</button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isNew && org?.members !== undefined && (
                <div className="mt-8 bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Kullanıcılar</h2>

                    <form onSubmit={handleAddUser} className="mb-6 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                        <h3 className="text-sm font-medium text-slate-300 mb-3">Yeni Kullanıcı Ekle</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <input
                                value={addUserForm.email}
                                onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                                type="email"
                                required
                                placeholder="E-posta *"
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                            />
                            <input
                                value={addUserForm.password}
                                onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
                                type="password"
                                placeholder="Şifre (yeni kullanıcı için)"
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                            />
                            <input
                                value={addUserForm.first_name}
                                onChange={(e) => setAddUserForm({ ...addUserForm, first_name: e.target.value })}
                                placeholder="Ad"
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                            />
                            <input
                                value={addUserForm.last_name}
                                onChange={(e) => setAddUserForm({ ...addUserForm, last_name: e.target.value })}
                                placeholder="Soyad"
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                            />
                            <input
                                value={addUserForm.phone}
                                onChange={(e) => setAddUserForm({ ...addUserForm, phone: e.target.value })}
                                placeholder="Telefon"
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                            />
                            <input
                                value={addUserForm.job_title}
                                onChange={(e) => setAddUserForm({ ...addUserForm, job_title: e.target.value })}
                                placeholder="Görevi"
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                            />
                            <select
                                value={addUserForm.role}
                                onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value })}
                                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            >
                                {roleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <label className="flex items-center gap-2 text-slate-300">
                                <input
                                    type="checkbox"
                                    checked={addUserForm.is_payment_responsible}
                                    onChange={(e) => setAddUserForm({ ...addUserForm, is_payment_responsible: e.target.checked })}
                                    className="rounded"
                                />
                                Ödeme sorumlusu
                            </label>
                        </div>
                        <button
                            type="submit"
                            disabled={addingUser}
                            className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm disabled:opacity-50"
                        >
                            {addingUser ? 'Ekleniyor...' : 'Ekle'}
                        </button>
                    </form>

                    <table className="w-full">
                        <thead>
                            <tr className="text-left text-slate-400 text-sm">
                                <th className="pb-2">Ad Soyad</th>
                                <th className="pb-2">E-posta</th>
                                <th className="pb-2">Telefon</th>
                                <th className="pb-2">Görevi</th>
                                <th className="pb-2">Rol</th>
                                <th className="pb-2">Ödeme</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {org.members.map((m) => (
                                <tr key={m.id} className="border-t border-slate-700">
                                    {editingUser?.user_id === m.user_id ? (
                                        <td colSpan={7} className="py-2">
                                            <div className="flex gap-2 flex-wrap items-center">
                                                <select value={editingUser.role} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })} className="px-2 py-1 bg-slate-700 rounded text-white text-sm">
                                                    {roleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </select>
                                                <input value={editingUser.job_title || ''} onChange={(e) => setEditingUser({ ...editingUser, job_title: e.target.value })} placeholder="Görevi" className="px-2 py-1 bg-slate-700 rounded text-white text-sm w-28" />
                                                <label className="flex items-center gap-1 text-slate-300 text-sm">
                                                    <input type="checkbox" checked={!!editingUser.is_payment_responsible} onChange={(e) => setEditingUser({ ...editingUser, is_payment_responsible: e.target.checked })} className="rounded" />
                                                    Ödeme sorumlusu
                                                </label>
                                                <button onClick={() => handleUpdateUser(m.user_id, { role: editingUser.role, job_title: editingUser.job_title, is_payment_responsible: editingUser.is_payment_responsible })} className="px-2 py-1 bg-amber-600 text-white rounded text-xs">Kaydet</button>
                                                <button onClick={() => setEditingUser(null)} className="px-2 py-1 bg-slate-600 text-white rounded text-xs">İptal</button>
                                            </div>
                                        </td>
                                    ) : (
                                        <>
                                            <td className="py-2 text-white">{[m.first_name, m.last_name].filter(Boolean).join(' ') || '-'}</td>
                                            <td className="py-2 text-slate-300">{m.email}</td>
                                            <td className="py-2 text-slate-400">{m.phone || '-'}</td>
                                            <td className="py-2 text-slate-400">{m.job_title || '-'}</td>
                                            <td className="py-2 text-slate-400">{m.role}</td>
                                            <td className="py-2">
                                                {m.is_payment_responsible ? (
                                                    <span className="text-amber-400 text-xs">Sorumlu</span>
                                                ) : '-'}
                                            </td>
                                            <td className="py-2">
                                                <button onClick={() => setEditingUser({ user_id: m.user_id, role: m.role, job_title: m.job_title, is_payment_responsible: m.is_payment_responsible })} className="text-amber-400 hover:text-amber-300 text-sm mr-2">Düzenle</button>
                                                <button onClick={() => handleRemoveUser(m.user_id)} className="text-red-400 hover:text-red-300 text-sm">Çıkar</button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {org.members.length === 0 && (
                        <p className="text-slate-500 text-sm py-4">Henüz kullanıcı yok. İlk eklenen kullanıcı ödeme sorumlusu olur.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default OrganizationDetailPage;
